import { HYPES, type HypeKind } from './moods.js';
import type { Look, LookStats } from './schemas.js';

export function formatPrice(cents: number | null, currency = 'USD'): string {
  if (cents === null) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function formatCount(value: number): string {
  if (value < 1000) return String(value);
  if (value < 10_000) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  if (value < 1_000_000) return `${Math.round(value / 1000)}k`;
  return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
}

export function timeAgo(timestamp: number, now = Date.now()): string {
  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** The loudest reaction on a look — what we surface first in the feed. */
export function topHype(stats: LookStats): { kind: HypeKind; count: number } | null {
  const entries = Object.entries(stats.hype ?? {}) as [HypeKind, number][];
  const best = entries.filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])[0];
  return best ? { kind: best[0], count: best[1] } : null;
}

/** "maya and 12 others are obsessed" */
export function hypeSummary(stats: LookStats, firstName?: string): string {
  const top = topHype(stats);
  if (!top) return '';
  const hype = HYPES[top.kind];
  const others = top.count - 1;
  if (!firstName) return `${formatCount(top.count)} ${hype.label.toLowerCase()}`;
  if (others <= 0) return `${firstName} ${hype.phrase}`;
  return `${firstName} and ${formatCount(others)} other${others === 1 ? '' : 's'} ${hype.phrase}`;
}

/**
 * Fallback fills for users with no photo.
 *
 * These render on screen, so they follow the palette rather than predating it:
 * ink, oxblood and bone, mixed the way an ink would be. The old set was the
 * pre-redirection neon (#FF4D8D → #7C5CFF) and was the last place it survived.
 */
const AVATAR_GRADIENTS: [string, string][] = [
  ['#7E2436', '#4A121F'],
  ['#2A2825', '#0B0B0C'],
  ['#A8404F', '#7E2436'],
  ['#575249', '#2A2825'],
  ['#4A121F', '#131313'],
  ['#8A857B', '#575249'],
];

/** Deterministic gradient for users without an avatar. */
export function avatarGradient(seed: string): [string, string] {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

/** Room codes are read aloud, so no ambiguous glyphs. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Quoted in the join form's hint and used for its `maxLength`. */
export const ROOM_CODE_LENGTH = 6;

export function generateRoomCode(random: () => number = Math.random): string {
  let out = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    out += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)];
  }
  return out;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Strip a data: URL prefix, returning raw base64 and the detected mime type. */
export function parseDataUrl(value: string): { base64: string; mimeType: string } {
  const match = /^data:([^;,]+);base64,(.*)$/s.exec(value);
  if (match) return { mimeType: match[1], base64: match[2] };
  return { mimeType: 'image/jpeg', base64: value };
}

/**
 * The local, instant version of what the server does to a look when it is hyped.
 *
 * Shared because web and mobile both apply it optimistically and both have to
 * undo it the same way. Note the no-op guard: when the look is already in the
 * requested state this returns the input unchanged, which means the inverse
 * call is NOT a valid rollback unless the forward call actually changed
 * something. Callers must check before undoing.
 */
export function applyHype(look: Look, kind: HypeKind, on: boolean): Look {
  if (look.viewerHype.includes(kind) === on) return look;
  const delta = on ? 1 : -1;
  const hype = { ...(look.stats.hype ?? {}) } as Record<HypeKind, number>;
  hype[kind] = Math.max(0, (hype[kind] ?? 0) + delta);
  return {
    ...look,
    stats: {
      ...look.stats,
      hype,
      totalHype: Math.max(0, look.stats.totalHype + delta),
    },
    viewerHype: on ? [...look.viewerHype, kind] : look.viewerHype.filter((k) => k !== kind),
  };
}
